import { basicAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const device42 = defineConnector({
  identifier: "device42",
  name: "Device42",
  tagLine: "IT asset discovery and dependency mapping",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: ({ baseUrl }) =>
    exactUrlToRegex(baseUrl || "https://device42.example.com/api/2.0"),
  auth: basicAuth({
    username: { from: "secret", field: "username" },
    password: { from: "secret", field: "password" },
  }),
  steps: [
    {
      title: "Set up Device42",
      items: [
        {
          kind: "markdown",
          content:
            "Create a Device42 API user with view access to the devices you want to query. Use your appliance’s HTTPS address followed by **/api/2.0**. Enter the API user’s credentials below. See the [Device42 API reference](https://api.device42.com/).",
        },
      ],
    },
    {
      title: "Enter connection details",
      items: [
        {
          kind: "url",
          label: "API base URL",
          required: true,
          placeholder: "https://device42.example.com/api/2.0",
          target: {
            kind: "configuration",
            field: "baseUrl",
          },
          helperText:
            "Device42 is commonly deployed as an appliance. Use the appliance’s HTTPS IP address or FQDN, then append `/api/2.0`; for example, `https://device42.example.com/api/2.0`. Device42’s documentation does not identify regional API endpoints. The documented `https://api.device42.com` host is the public API-reference example, not a customer’s self-hosted appliance URL.",
        },
        {
          kind: "input",
          label: "Device42 username",
          required: true,
          target: {
            kind: "secrets",
            field: "username",
          },
          helperText:
            "A Device42 user permitted to read the Device, Subnet, and Customer objects required by the connector. Device42 documents that API access follows the user’s role-based security.",
        },
        {
          kind: "input",
          label: "Device42 password",
          required: true,
          target: {
            kind: "secrets",
            field: "password",
          },
          helperText:
            "The password for the Device42 API user. Do not use the public Swagger demonstration credentials.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) => ({
      url:
        (baseUrl || "https://device42.example.com/api/2.0")
          .trim()
          .replace(/\/+$/, "") + "/devices/?limit=1",
      ...{ method: "GET", headers: { Accept: "application/json" } },
    }),
  },
});
